

import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, FormEvent, KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout';
import { authApi } from '@/lib/api/auth.api';
import { useAuth } from '@/components/auth/AuthProvider';
import { useLoginSession } from '@/store/login-session.store';
import { isMobileAuthFlow, redirectToMobileApp } from '@/utils/mobile-auth';
import { startProviderRedirect } from '@/lib/utils/oauth-connect';
import { isTauri } from '@/lib/utils/tauri';
import type { User } from '@scrambl/shared';
import { AppLoader } from '@/lib/router/components';
import { createLogger } from '@/lib/logger';
import {
  acceptInviteForType,
  parseInviteType,
} from '@/utils/auth/invite-flow';

const logger = createLogger('pages/register/confirmation/index');

// Import shadcn components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
// Define types for the API response data
type AuthUrlResponse = { authUrl: string };
function isAuthUrlData(data: unknown): data is AuthUrlResponse {
  return Boolean(data && typeof data === 'object' && 'authUrl' in data);
}

function isUserData(data: unknown): data is User {
  return Boolean(data && typeof data === 'object' && 'id' in data && 'email' in data);
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

// Only honor same-origin absolute paths so a crafted returnUrl can't turn this
// into an open redirect. Rejects protocol-relative (`//host`) and absolute URLs.
function safeInternalPath(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  return null;
}

export default function RegistrationConfirmationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isValidatingToken] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const { isAuthenticated, isLoading: authLoading, setUser } = useAuth();
  const isAddAccountFlow = searchParams.has('addAccount');
  const [inviteInfo] = useState<{ organizationName?: string } | null>(null);
  const [isAlreadyConfirmed, setIsAlreadyConfirmed] = useState(false);
  const [isPlaceholderSession, setIsPlaceholderSession] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get session data from Zustand store using individual selectors to avoid infinite loops
  const sessionEmail = useLoginSession(state => state.email);
  const sessionValue = useLoginSession(state => state.session);
  const sessionInviteToken = useLoginSession(state => state.inviteToken);
  const sessionInviteType = useLoginSession(state => state.inviteType);
  const sessionRedirectUrl = useLoginSession(state => state.redirectUrl);
  const clearSession = useLoginSession(state => state.clearSession);
  
  useEffect(() => {
    // If we don't have email in the store, and we're not in the verification process, redirect back to register
    if (!sessionEmail && !isVerifying) {
      navigate(isAddAccountFlow ? '/register?addAccount=true' : '/register');
      return;
    }

    // Check if we're using a placeholder session
    if (sessionValue === 'pending-confirmation') {
      setIsPlaceholderSession(true);
    }

    // We can proceed even with a placeholder session value
    setEmail(normalizeEmail(sessionEmail));
    setSession(sessionValue);
    setInviteToken(sessionInviteToken || null);
    setRedirectUrl(safeInternalPath(sessionRedirectUrl));
  }, [sessionEmail, sessionValue, sessionInviteToken, sessionRedirectUrl, navigate, isVerifying, isAddAccountFlow]);
  
  // Redirect authenticated users, but only if not in verification process
  useEffect(() => {
    if (isAuthenticated && !authLoading && !isVerifying && !isAddAccountFlow) {
      if (redirectUrl) {
        // Explicit return target (e.g. client-portal invite round-trip).
        navigate(redirectUrl, { replace: true });
      } else if (inviteToken) {
        // If there's an invite token, redirect to organizations page
        navigate('/organizations', { replace: true });
      } else {
        // Default redirect
        navigate('/today', { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, inviteToken, redirectUrl, navigate, isVerifying, isAddAccountFlow]);
  
  // Clean up localStorage when component unmounts
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && !isVerifying) {
        localStorage.removeItem('registrationEmail');
        localStorage.removeItem('registrationInviteToken');
      }
    };
  }, [isVerifying]);

  useEffect(() => {
    if (timeLeft > 0 && !canResend) {
      const timerId = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    } else if (timeLeft === 0 && !canResend) {
      setCanResend(true);
    }
  }, [timeLeft, canResend]);

  // Initialize input refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Auto-focus the first input field when component mounts
  useEffect(() => {
    // Short timeout to ensure DOM is fully loaded
    const timer = setTimeout(() => {
      if (inputRefs.current[0] && !isLoading && !isVerifying) {
        inputRefs.current[0].focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isLoading, isVerifying]);

  // Placeholder session behavior is tracked via isPlaceholderSession state
  // No additional effect logic needed - session state is handled elsewhere

  const handleDigitChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d+$/.test(value)) return;

    // Clear error state when user starts typing
    if (hasError) {
      setHasError(false);
    }

    // Update the code
    const newCode = code.split('');
    newCode[index] = value.charAt(0); // Just take the first character
    setCode(newCode.join(''));

    // Focus next input if there's a value and not the last digit
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Go to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const cleanedData = pastedData.replace(/\D/g, '').substring(0, 6);
    
    if (cleanedData) {
      // Fill all inputs with pasted digits
      const newCode = cleanedData.padEnd(6, ' ').substring(0, 6).split('');
      setCode(newCode.join('').trim());
      
      // Focus appropriate field
      if (cleanedData.length < 6) {
        inputRefs.current[cleanedData.length]?.focus();
      } else {
        // Focus submit button if complete
        const submitBtn = document.querySelector('button[type="submit"]') as HTMLButtonElement;
        submitBtn?.focus();
      }
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !code || code.replace(/\s/g, '').length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    try {
      setIsLoading(true);
      setIsVerifying(true);
      setHasError(false);
      const isMobile = isMobileAuthFlow();
      const platform = isTauri() ? 'desktop' : isMobile ? 'mobile' : undefined;
      const response = await authApi.confirmSignUp(
        email,
        code,
        inviteToken || undefined,
        platform,
        session && session !== 'pending-confirmation' ? session : undefined,
      );
      
      if (response.success) {
        // Mobile web-auth flow: return tokens to the app instead of completing web session.
        if (isMobile && response.tokens) {
          clearSession();
          const redirected = redirectToMobileApp({
            accessToken: response.tokens.accessToken,
            refreshToken: response.tokens.refreshToken,
            expiresIn: response.tokens.expiresIn,
          });

          if (redirected) {
            return;
          }
          logger.warn('[RegistrationConfirmation] Mobile redirect failed, falling back to web flow');
        }

        // Set the user in the auth context
        if (response.data) {
          if (isUserData(response.data)) {
            // Check if auto-login succeeded (cookies were set) or if user needs to log in manually
            // When the backend can't auto-login (e.g. server restarted between register and confirm),
            // it returns success with user data but no cookies/tokens
            const needsManualLogin = response.message?.includes('Please log in');

            if (needsManualLogin) {
              clearSession();
              toast.success('Email verified! Please log in to continue.');
              const loginParams = new URLSearchParams({ email });
              if (inviteToken) {
                loginParams.set('token', inviteToken);
                loginParams.set(
                  'inviteType',
                  sessionInviteType || 'organization',
                );
              }
              if (redirectUrl) {
                loginParams.set('returnUrl', redirectUrl);
              }
              if (isAddAccountFlow) {
                loginParams.set('addAccount', 'true');
              }
              navigate(`/login?${loginParams.toString()}`);
              return;
            }

            // Mark verification as successful - this shows the loading logo immediately
            setVerificationSuccess(true);

            // Set user in the auth context
            setUser(response.data);

            // Check for pending invite token
            const pendingInviteToken = inviteToken || localStorage.getItem('pendingInviteToken');
            if (pendingInviteToken) {
              try {
                const pendingInviteType =
                  sessionInviteType ||
                  parseInviteType(localStorage.getItem('pendingInviteType'));
                await acceptInviteForType(
                  pendingInviteToken,
                  pendingInviteType,
                );

                // Clear the pending invite token
                localStorage.removeItem('pendingInviteToken');
                localStorage.removeItem('pendingInviteType');
              } catch (inviteError) {
                logger.error('[RegistrationConfirmation] Error accepting invitation:', inviteError);
              }
            }

            // Clear the session after a delay to prevent issues
            setTimeout(() => {
              clearSession();
            }, 500);

            // Use direct navigation to dashboard or organizations page
            setTimeout(() => {
              // For add account flow, simply redirect to /today
              if (isAddAccountFlow) {
                window.location.href = '/today?auth_success=true';
              } else if (redirectUrl) {
                // Explicit return target (e.g. client-portal invite round-trip),
                // where the portal token is redeemed now that we're signed in.
                window.location.href = redirectUrl;
              } else if (inviteToken || localStorage.getItem('pendingInviteToken')) {
                window.location.href = '/organizations';
              } else {
                window.location.href = '/today';
              }
            }, 100);
          } else if (isAuthUrlData(response.data)) {
            // Hand the provider's sign-in page to the real browser on desktop;
            // the platform marker above makes its callback deep link back.
            await startProviderRedirect(response.data.authUrl);
          }
        }
      } else {
        // Check if the error is because the user is already confirmed
        if (response.message?.includes('already confirmed')) {
          setIsAlreadyConfirmed(true);
          setIsLoading(false);
          setIsVerifying(false);
          return;
        }

        logger.error('[RegistrationConfirmation] Registration confirmation failed:', response.message);
        toast.error(response.message || 'Verification failed. Please try again.');
        setHasError(true);
        setIsLoading(false);
        setIsVerifying(false);
      }
    } catch (error) {
      logger.error('[RegistrationConfirmation] Error during verification:', error);
      toast.error('An error occurred during verification. Please try again.');
      setHasError(true);
      setIsLoading(false);
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error('Email is required');
      navigate(isAddAccountFlow ? '/register?addAccount=true' : '/register');
      return;
    }

    try {
      setIsResending(true);
      const response = await authApi.resendConfirmationCode(email);
      
      if (response.success) {
        toast.success('Verification code resent. Please check your email.');
        setTimeLeft(30);
        setCanResend(false);
        // Clear the current code
        setCode('');
      } else {
        toast.error(response.message || 'Failed to resend code');
      }
    } catch (error) {
      logger.error('Error resending code:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = () => {
    // Clear the session data from the store
    clearSession();
    
    // Clear any localStorage items related to registration
    if (typeof window !== 'undefined') {
      localStorage.removeItem('registrationEmail');
      localStorage.removeItem('registrationInviteToken');
    }
    
    navigate(isAddAccountFlow ? '/register?addAccount=true' : '/register');
  };

  // Show full-screen loading logo after successful verification
  // This provides a smooth transition while navigating to user setup
  if (verificationSuccess) {
    return <AppLoader size="lg" />;
  }

  // Show loading state while checking auth
  if (authLoading && !isVerifying) {
    return (
      <SplitAuthLayout pageType="register-confirmation">
        <div className="flex justify-center items-center py-8">
          <AppLoader inline size="md" />
        </div>
      </SplitAuthLayout>
    );
  }

  // Render loading state if validating token
  if (isValidatingToken && !isVerifying) {
    return (
      <SplitAuthLayout pageType="register-confirmation">
        <div className="text-center">
          <h1 className="text-xl font-medium mb-4">Validating Invitation</h1>
          <AppLoader inline size="md" />
        </div>
      </SplitAuthLayout>
    );
  }

  // If user is already confirmed, show a message directing them to login
  if (isAlreadyConfirmed) {
    return (
      <SplitAuthLayout pageType="register-confirmation" customHeadline="You're all set!" customSubheadline="Your account has been confirmed. Time to sign in and get started!">
        <div className="w-full">
          <div className="text-center mb-6">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/20 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                <path d="m9 12 2 2 4-4"></path>
              </svg>
            </div>
            <h1 className="text-xl font-semibold mb-2 text-foreground">Account Already Confirmed</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Your account has already been confirmed. You can now log in.
            </p>
          </div>

          <Button
            onClick={() => navigate(isAddAccountFlow ? '/login?addAccount=true' : '/login')}
            className="w-full h-11 font-medium"
          >
            Go to Login
          </Button>
        </div>
      </SplitAuthLayout>
    );
  }

  // Custom headline for invite flow
  const customHeadline = inviteToken && inviteInfo?.organizationName
    ? `Join ${inviteInfo.organizationName}`
    : undefined;

  return (
    <SplitAuthLayout pageType="register-confirmation" customHeadline={customHeadline}>
      <div className="w-full">
        {/* Email display */}
        <div className="text-center mb-6">
          <p className="text-sm text-muted-foreground">
            Code sent to <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-center gap-2 sm:gap-3">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Input
                  key={index}
                  ref={el => { inputRefs.current[index] = el }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-semibold bg-background border-input ${hasError ? 'border-destructive focus-visible:ring-destructive/20' : ''}`}
                  value={code[index] || ''}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isLoading}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 font-medium"
            disabled={isLoading || isResending || code.replace(/\s/g, '').length !== 6}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                <span>Verifying...</span>
              </span>
            ) : "Complete Registration"}
          </Button>
        </form>

        <div className="flex items-center justify-between mt-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={isLoading || isResending}
            className="text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={handleResendCode}
            disabled={isResending || timeLeft > 0 || isLoading}
            className="text-primary h-auto p-0 hover:text-primary/80"
          >
            {isResending ? "Sending..." : timeLeft > 0 ? `Resend in ${timeLeft}s` : "Resend code"}
          </Button>
        </div>
      </div>
    </SplitAuthLayout>
  );
} 
